import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';

interface DateTime {
  name: string;
  short_name: string;
  key: string;
  id: number;
}

export const MINUTES_IN_HOUR = 60;
export const SECONDS_IN_MINUTE = 60;
export const MILLISECONDS_IN_SECOND = 1000;
export const SECONDS_IN_DAY = 24 * 60 * 60;

enum MONTH_NAME {
  JAN = 'jan',
  FEB = 'feb',
  MAR = 'mar',
  APR = 'apr',
  MAY = 'may',
  JUN = 'jun',
  JUL = 'jul',
  AUG = 'aug',
  SEP = 'sep',
  OCT = 'oct',
  NOV = 'nov',
  DEC = 'dec'
}

const MONTHS_INDEX = {
  [MONTH_NAME.JAN]: 1,
  [MONTH_NAME.FEB]: 2,
  [MONTH_NAME.MAR]: 3,
  [MONTH_NAME.APR]: 4,
  [MONTH_NAME.MAY]: 5,
  [MONTH_NAME.JUN]: 6,
  [MONTH_NAME.JUL]: 7,
  [MONTH_NAME.AUG]: 8,
  [MONTH_NAME.SEP]: 9,
  [MONTH_NAME.OCT]: 10,
  [MONTH_NAME.NOV]: 11,
  [MONTH_NAME.DEC]: 12
};

enum WEEKDAY_NAMES {
  MON = 'mon',
  TUE = 'tue',
  WED = 'wed',
  THU = 'thu',
  FRI = 'fri',
  SAT = 'sat',
  SUN = 'sun',
}

const WEEKDAYS_INDEX = {
  [WEEKDAY_NAMES.MON]: 0,
  [WEEKDAY_NAMES.TUE]: 1,
  [WEEKDAY_NAMES.WED]: 2,
  [WEEKDAY_NAMES.THU]: 3,
  [WEEKDAY_NAMES.FRI]: 4,
  [WEEKDAY_NAMES.SAT]: 5,
  [WEEKDAY_NAMES.SUN]: 6,
};

export const SHORT_MONTHS = computedLocale(() => ({
  [MONTH_NAME.JAN]: i18n.t('Jan'),
  [MONTH_NAME.FEB]: i18n.t('Feb'),
  [MONTH_NAME.MAR]: i18n.t('Mar'),
  [MONTH_NAME.APR]: i18n.t('Apr'),
  [MONTH_NAME.MAY]: i18n.t('May'),
  [MONTH_NAME.JUN]: i18n.t('Jun'),
  [MONTH_NAME.JUL]: i18n.t('Jul'),
  [MONTH_NAME.AUG]: i18n.t('Aug'),
  [MONTH_NAME.SEP]: i18n.t('Sep'),
  [MONTH_NAME.OCT]: i18n.t('Oct'),
  [MONTH_NAME.NOV]: i18n.t('Nov'),
  [MONTH_NAME.DEC]: i18n.t('Dec'),
}));

export const FULL_MONTHS = computedLocale(() => ({
  [MONTH_NAME.JAN]: i18n.t('January'),
  [MONTH_NAME.FEB]: i18n.t('February'),
  [MONTH_NAME.MAR]: i18n.t('March'),
  [MONTH_NAME.APR]: i18n.t('April'),
  [MONTH_NAME.MAY]: i18n.t('May'),
  [MONTH_NAME.JUN]: i18n.t('June'),
  [MONTH_NAME.JUL]: i18n.t('July'),
  [MONTH_NAME.AUG]: i18n.t('August'),
  [MONTH_NAME.SEP]: i18n.t('September'),
  [MONTH_NAME.OCT]: i18n.t('October'),
  [MONTH_NAME.NOV]: i18n.t('November'),
  [MONTH_NAME.DEC]: i18n.t('December')
}));

export const SHORT_WEEKDAYS = computedLocale(() => ({
  [WEEKDAY_NAMES.SUN]: i18n.t('Sun'),
  [WEEKDAY_NAMES.MON]: i18n.t('Mon'),
  [WEEKDAY_NAMES.TUE]: i18n.t('Tue'),
  [WEEKDAY_NAMES.WED]: i18n.t('Wed'),
  [WEEKDAY_NAMES.THU]: i18n.t('Thu'),
  [WEEKDAY_NAMES.FRI]: i18n.t('Fri'),
  [WEEKDAY_NAMES.SAT]: i18n.t('Sat')
}));

export const FULL_WEEKDAYS = computedLocale(() => ({
  [WEEKDAY_NAMES.SUN]: i18n.t('Sunday'),
  [WEEKDAY_NAMES.MON]: i18n.t('Monday'),
  [WEEKDAY_NAMES.TUE]: i18n.t('Tuesday'),
  [WEEKDAY_NAMES.WED]: i18n.t('Wednesday'),
  [WEEKDAY_NAMES.THU]: i18n.t('Thursday'),
  [WEEKDAY_NAMES.FRI]: i18n.t('Friday'),
  [WEEKDAY_NAMES.SAT]: i18n.t('Saturday')
}));

export const SCHEDULE_MONTH: DateTime[] = computedLocale(() => ([
  {
    name: FULL_MONTHS[MONTH_NAME.JAN],
    short_name: SHORT_MONTHS[MONTH_NAME.JAN],
    key: MONTH_NAME.JAN,
    id: MONTHS_INDEX[MONTH_NAME.JAN],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.FEB],
    short_name: SHORT_MONTHS[MONTH_NAME.FEB],
    key: MONTH_NAME.FEB,
    id: MONTHS_INDEX[MONTH_NAME.FEB],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.MAR],
    short_name: SHORT_MONTHS[MONTH_NAME.MAR],
    key: MONTH_NAME.MAR,
    id: MONTHS_INDEX[MONTH_NAME.MAR],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.APR],
    short_name: SHORT_MONTHS[MONTH_NAME.APR],
    key: MONTH_NAME.APR,
    id: MONTHS_INDEX[MONTH_NAME.APR],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.MAY],
    short_name: SHORT_MONTHS[MONTH_NAME.MAY],
    key: MONTH_NAME.MAY,
    id: MONTHS_INDEX[MONTH_NAME.MAY],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.JUN],
    short_name: SHORT_MONTHS[MONTH_NAME.JUN],
    key: MONTH_NAME.JUN,
    id: MONTHS_INDEX[MONTH_NAME.JUN],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.JUL],
    short_name: SHORT_MONTHS[MONTH_NAME.JUL],
    key: MONTH_NAME.JUL,
    id: MONTHS_INDEX[MONTH_NAME.JUL],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.AUG],
    short_name: SHORT_MONTHS[MONTH_NAME.AUG],
    key: MONTH_NAME.AUG,
    id: MONTHS_INDEX[MONTH_NAME.AUG],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.SEP],
    short_name: SHORT_MONTHS[MONTH_NAME.SEP],
    key: MONTH_NAME.SEP,
    id: MONTHS_INDEX[MONTH_NAME.SEP],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.OCT],
    short_name: SHORT_MONTHS[MONTH_NAME.OCT],
    key: MONTH_NAME.OCT,
    id: MONTHS_INDEX[MONTH_NAME.OCT],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.NOV],
    short_name: SHORT_MONTHS[MONTH_NAME.NOV],
    key: MONTH_NAME.NOV,
    id: MONTHS_INDEX[MONTH_NAME.NOV],
  },
  {
    name: FULL_MONTHS[MONTH_NAME.DEC],
    short_name: SHORT_MONTHS[MONTH_NAME.DEC],
    key: MONTH_NAME.DEC,
    id: MONTHS_INDEX[MONTH_NAME.DEC],
  },
]));

export const SCHEDULE_WEEKDAYS: DateTime[] = computedLocale(() => ([
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.MON],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.MON],
    key: WEEKDAY_NAMES.MON,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.MON]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.TUE],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.TUE],
    key: WEEKDAY_NAMES.TUE,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.TUE]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.WED],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.WED],
    key: WEEKDAY_NAMES.WED,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.WED]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.THU],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.THU],
    key: WEEKDAY_NAMES.THU,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.THU]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.FRI],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.FRI],
    key: WEEKDAY_NAMES.FRI,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.FRI]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.SAT],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.SAT],
    key: WEEKDAY_NAMES.SAT,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.SAT]
  },
  {
    name: FULL_WEEKDAYS[WEEKDAY_NAMES.SUN],
    short_name: SHORT_WEEKDAYS[WEEKDAY_NAMES.SUN],
    key: WEEKDAY_NAMES.SUN,
    id: WEEKDAYS_INDEX[WEEKDAY_NAMES.SUN]
  }
]));
